/**
 * メッセージバブルコンポーネント
 * 個別メッセージの表示
 */

import React from 'react';
import { Box, Paper, Typography, Avatar, Chip, Tooltip } from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  ThreadMessage,
  SenderType,
  MessageStatus,
  ReplyType,
} from '../../types/message-thread';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { LinkifiedText } from '../Common/LinkifiedText';

interface MessageBubbleProps {
  message: ThreadMessage;
  isOwn: boolean;
  showAvatar: boolean;
  userType: 'company' | 'student';
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  showAvatar,
  userType,
}) => {
  const formatMessageTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd HH:mm', { locale: ja });
    } catch {
      return '不明';
    }
  };

  const getReplyTypeText = (replyType?: ReplyType) => {
    switch (replyType) {
      case ReplyType.POSITIVE:
        return '興味あり';
      case ReplyType.NEGATIVE:
        return '辞退';
      case ReplyType.QUESTION:
        return '質問';
      case ReplyType.OTHER:
        return 'その他';
      default:
        return null;
    }
  };

  const getReplyTypeColor = (replyType?: ReplyType) => {
    switch (replyType) {
      case ReplyType.POSITIVE:
        return 'success';
      case ReplyType.NEGATIVE:
        return 'error';
      case ReplyType.QUESTION:
        return 'info';
      case ReplyType.OTHER:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: MessageStatus) => {
    switch (status) {
      case MessageStatus.READ:
        return <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />;
      case MessageStatus.DELIVERED:
        return <CheckCircleIcon sx={{ fontSize: 14, color: 'grey.500' }} />;
      case MessageStatus.SENT:
        return <ScheduleIcon sx={{ fontSize: 14, color: 'grey.400' }} />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 1,
        mb: 2,
        px: 2,
        pl: showAvatar ? 2 : 6,
        pr: showAvatar ? 2 : 6,
      }}
    >
      {/* アバター */}
      {showAvatar && (
        <Avatar
          src={message.sender?.avatarUrl}
          alt={message.sender?.name}
          sx={{
            width: 32,
            height: 32,
            bgcolor: isOwn ? 'primary.main' : 'grey.400',
          }}
        >
          {message.senderType === SenderType.COMPANY ? (
            <BusinessIcon sx={{ fontSize: 18 }} />
          ) : (
            <PersonIcon sx={{ fontSize: 18 }} />
          )}
        </Avatar>
      )}

      {/* メッセージ本体 */}
      <Box
        sx={{
          maxWidth: { xs: '90%', sm: '85%', md: '80%' },
          minWidth: '120px',
        }}
      >
        {/* 送信者名・時刻 */}
        {showAvatar && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5,
              justifyContent: isOwn ? 'flex-end' : 'flex-start',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {message.sender?.name || (isOwn ? 'あなた' : '送信者不明')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMessageTime(message.sentAt)}
            </Typography>
          </Box>
        )}

        {/* メッセージバブル */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            backgroundColor: isOwn ? 'primary.50' : 'white',
            borderRadius: 2,
            borderTopLeftRadius: isOwn ? 2 : showAvatar ? 0.5 : 2,
            borderTopRightRadius: isOwn ? (showAvatar ? 0.5 : 2) : 2,
          }}
        >
          {/* 返信タイプ */}
          {message.replyType && (
            <Box sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={getReplyTypeText(message.replyType)}
                color={getReplyTypeColor(message.replyType)}
                variant="outlined"
              />
            </Box>
          )}

          {/* メッセージ本文 */}
          <LinkifiedText
            text={message.messageBody}
            variant="body2"
            sx={{
              wordBreak: 'break-word',
              lineHeight: 1.5,
            }}
          />

          {/* メッセージ状態・時刻 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              mt: 1,
            }}
          >
            {!showAvatar && (
              <Typography variant="caption" color="text.secondary">
                {formatMessageTime(message.sentAt)}
              </Typography>
            )}
            {isOwn && (
              <Tooltip title={`状態: ${message.status}`}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getStatusIcon(message.status)}
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* 既読時刻 */}
          {message.readAt && isOwn && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
            >
              既読: {formatMessageTime(message.readAt)}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
